/* eslint-disable curly, no-unused-expressions, no-magic-numbers */
import ResizeTracker from 'utils/ResizeTracker';

/**
 * @module FaqCategories
 */
class FaqCategories {

    /**
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - ConditionerJS's merged options
     */
    constructor(element, options) {
        this._element = element;
        this._options = options;

        this._state = {
            maxResults: 0,
            results: 0,
            isMobile: false,
            headerHeight: 0,
            filtersVisible: false,
            mobileSize: 768,
            activeCategories: []
        };

        this._rootContainer = document.body;

        this._elements = {
            categoryItems: Array.from(this._element.querySelectorAll(this._options.categoryItems)),
            categoryItemsLinks: Array.from(this._element.querySelectorAll(this._options.categoryItemsLinks)),
            sidebarList: this._element.querySelector(this._options.sidebarList),
            filterToggle: this._element.querySelector(this._options.filterToggle),
            sections: Array.from(this._element.querySelectorAll(this._options.sections)),
            resetButton: this._element.querySelector(this._options.resetButton),
            header: document.querySelector('.header')
        };

        this._liveRegion = {
            container: document.querySelector(`#${this._options.liveRegion}`),
            counter: document.querySelector(`#${this._options.liveRegion} .counter`),
            tag: document.querySelector(`#${this._options.liveRegion} .tag`),
            singleText: document.querySelector(`#${this._options.liveRegion} .short-text`),
            multipleText: document.querySelector(`#${this._options.liveRegion} .long-text`),
            allResults: document.querySelector(`#${this._options.liveRegion} .all-results`),
            noResult: document.querySelector(`#${this._options.liveRegion} .no-result`)
        };

        this._initialize();
    }

    /**
     * Initialize
     */
    _initialize() {
        this._resizeTracker = new ResizeTracker();
        this._resizeTracker.init(
            { element: this._rootContainer },
            this._setIsMobile
        );

        this._setIsMobile();

        // Add click event to all sidebar items
        this._elements.categoryItems.forEach(category => {
            category.addEventListener('click', event => this._clickedCategory(event));
        });

        // Prevent default behavior on anchor links
        this._elements.categoryItemsLinks.forEach(category => {
            category.addEventListener('click', event => event.preventDefault());
        });


        // Add click event the reset button
        this._elements.resetButton.addEventListener('click', event => this._resetCategories(event));
        this._elements.filterToggle.addEventListener('click', event => this._toggleFilter(event));

        this._initFilters();
    }


    /**
     * Set mobile state
     * @private
     */
    _setIsMobile() {
        if (!this._rootContainer) return;
        // eslint-disable-next-line no-magic-numbers
        const em = parseFloat(getComputedStyle(this._elements.header).fontSize) * 9;
        this._state.headerHeight = this._elements.header.offsetHeight + em;
        this._state.isMobile = this._rootContainer.clientWidth < this._state.mobileSize;
        this._state.isMobile && this._elements.filterToggle.setAttribute('aria-expanded', this._state.filtersVisible);
        this._state.isMobile && this._elements.sidebarList.setAttribute('aria-hidden', !this._state.filtersVisible);
        !this._state.isMobile && this._elements.sidebarList.hasAttribute('aria-hidden') && this._elements.sidebarList.removeAttribute('aria-hidden');
    }


    /**
     * When someone clicks on the category
     * @param {*} event the click event
     */
    _clickedCategory(event) {
        event.preventDefault();
        const isActive = event.currentTarget.classList.contains('active');
        const targetLink = event.currentTarget.querySelector('a');
        targetLink.setAttribute('aria-selected', !isActive);
        !isActive && event.currentTarget.classList.add('active');
        isActive && event.currentTarget.classList.remove('active');
        const hasActiveCategory = this._element.querySelector('.search-categories__sidebar-item.active');
        hasActiveCategory ? this._showActiveSection(event.currentTarget) : this._showAllSections();
    }

    /**
     * Toggle filters visibility
     * @param {*} event the click event
     */
    _toggleFilter(event) {
        event.preventDefault();
        this._state.filtersVisible = !this._state.filtersVisible;
        this._elements.filterToggle.setAttribute('aria-expanded', this._state.filtersVisible);
        this._elements.sidebarList.setAttribute('aria-hidden', !this._state.filtersVisible);
    }

    _initFilters() {
        const activeFilter = this._elements.sidebarList.querySelector('.search-categories__sidebar-item.active') instanceof HTMLElement;
        this._state.isMobile && this._elements.filterToggle.setAttribute('aria-expanded', activeFilter);
        this._state.isMobile && this._elements.sidebarList.setAttribute('aria-hidden', !activeFilter);
        this._elements.sections.forEach(section => {
            const questions = section.querySelectorAll('.search-categories__question-inside');
            this._state.results = this._state.results + questions.length;
        });
        this._state.maxResults = this._state.results;
        this._handleLiveRegionChange();
    }

    /**
     * Reset categories filter
     */
    _resetCategories() {
        this._elements.categoryItems.forEach(category => {
            category.classList.remove('active');
        });

        this._elements.categoryItemsLinks.forEach(category => {
            category.setAttribute('aria-selected', false);
        });

        this._showAllSections();
    }

    /**
     * Show / un-hide the active section
     * @param {element} activeCategory
     */
    _showActiveSection() {
        this._state.results = 0;
        this._elements.sections.forEach((section, index) => {
            const isActive = this._elements.categoryItems[index].classList.contains('active');
            this._state.results = isActive ? this._state.results + 1 : this._state.results;

            const sectionLink = section.querySelector('[data-trigger]');
            isActive && this._state.activeCategories.push(sectionLink.textContent);
            const questions = sectionLink.nextElementSibling.querySelectorAll('.search-categories__question-inside');
            this._state.results = isActive ? this._state.results + questions.length : this._state.results;

            sectionLink && sectionLink.setAttribute('aria-expanded', isActive);
            sectionLink && sectionLink.nextElementSibling.setAttribute('aria-hidden', !isActive);
            section.setAttribute('aria-hidden', !isActive);
        });
        this._element.setAttribute('data-filtered', true);
        this._handleLiveRegionChange();
    }

    /**
     * Add the 'hide' class to all section elements
     */
    _hideSections() {
        this._elements.sections.forEach(section => {
            section.setAttribute('aria-hidden', true);
            const sectionLink = section.querySelector('[data-trigger]');
            sectionLink && sectionLink.setAttribute('aria-expanded', false);
            sectionLink && sectionLink.nextElementSibling.setAttribute('aria-hidden', true);
            section.classList.remove('active');
        });
        this._state.activeCategories = [];
        this._state.results = 0;
        this._handleLiveRegionChange();
    }

    /**
     * Add the 'hide' class to all section elements
     */
    _showAllSections() {
        this._state.results = 0;
        this._elements.sections.forEach(section => {
            section.setAttribute('aria-hidden', false);
            const sectionLink = section.querySelector('[data-trigger]');
            sectionLink && sectionLink.setAttribute('aria-expanded', false);
            sectionLink && sectionLink.nextElementSibling.setAttribute('aria-hidden', true);
            const questions = sectionLink.nextElementSibling.querySelectorAll('.search-categories__question-inside');
            this._state.results = this._state.results + questions.length;
            section.classList.remove('active');
        });
        this._state.activeCategories = [];
        this._element.setAttribute('data-filtered', false);
        this._handleLiveRegionChange();

    }

    /**
     * Update the live region for screen readers
     * @param {boolean} hasResults
     */
    _handleLiveRegionChange() {
        const hasResults = this._state.results > 0;
        this._liveRegion.counter.textContent = this._state.results;
        this._liveRegion.counter.setAttribute('aria-hidden', !hasResults);
        const isMultiple = this._state.results > 1;
        const hideSingleText = this._state.results !== 1;
        const hideMultipleText = !hasResults || !isMultiple || this._state.results === this._state.maxResults;
        this._liveRegion.singleText.setAttribute('aria-hidden', hideSingleText);
        this._liveRegion.multipleText.setAttribute('aria-hidden', hideMultipleText);
        this._liveRegion.noResult.setAttribute('aria-hidden', hasResults);
        this._liveRegion.allResults.setAttribute('aria-hidden', this._state.results !== this._state.maxResults);
        this._liveRegion.tag.setAttribute('aria-hidden', this._state.activeCategories.length < 1 || this._state.results === this._state.maxResults);
        this._liveRegion.tag.textContent = this._state.activeCategories.join(', ');
    }

    /**
     * Fetch the content
     *
     * @private
     */
    _fetchContent() {
        const headers = this._state.isLocal ? {
            headers: { 'Content-Type': 'text/html; charset=utf-8' }
        } : { headers: { 'Content-Type': 'text/html; charset=utf-8' }, method: 'POST' };

        fetch(this._state.modalurl, { headers })
            .then(response => response.text())
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                this._state.modal.content = doc.querySelector('[data-modal-content]');
                this._state.modal.title = doc.querySelector('[data-modal-title]').innerHTML;
                this._element.setAttribute('data-ajax-type', this._state.modal.content.tagName.toLowerCase());
                this._buildModal();
            })
            .catch(() => {
                this._handleError();
            });
    }
}

export default FaqCategories;
/* eslint-enable */
